/**
 * useSeatManagement Hook
 * 좌석 관리 상태 관리 및 비즈니스 로직
 */
import { useState, useCallback } from 'react';
import { useRealTimeSeatManagement } from '../../../hooks/useRealTimeSeatManagement';
import { SeatManagementData as RealTimeSeatData } from '../../../types/seat-management';
import type {
  ManagedSeat,
  SeatStatus,
  SeatManagementStats,
} from '../models/SeatManagement';
import { seatManagementService } from '../services/SeatManagementService';

/**
 * useSeatManagement 커스텀 훅
 */
export const useSeatManagement = () => {
  // 실시간 좌석 관리 훅 사용
  const {
    seats: realTimeSeats,
    loading: isLoading,
    error,
    lastRefreshTime,
    statistics,
    actions: { executeOwnerAction, loadSeats },
  } = useRealTimeSeatManagement();

  // 로컬 상태
  const [selectedSeat, setSelectedSeat] = useState<RealTimeSeatData | null>(null);
  const [showSeatModal, setShowSeatModal] = useState(false);
  const [newReason, setNewReason] = useState('');

  /**
   * realTimeSeats를 ManagedSeat 형태로 변환
   */
  const seats: ManagedSeat[] = realTimeSeats.map((seat) => ({
    id: seat.seat_number,
    number: seat.seat_number.toString(),
    status: seat.is_available
      ? seat.is_out_of_order
        ? 'maintenance'
        : 'available'
      : 'occupied',
    user_name: seat.current_session?.user?.name,
    user_id: seat.current_session?.user?.id,
    start_time: seat.current_session?.check_in_time,
    zone: 'A', // 기본값
    type: 'standard', // 기본값
    disabled_reason: seat.is_out_of_order ? '점검 중' : undefined,
    maintenance_reason: seat.is_out_of_order ? '정기점검' : undefined,
    room_id: (seat as any).room_id, // 룸 ID 추가
  }));

  /**
   * 통계 데이터 변환
   */
  const stats: SeatManagementStats = {
    total_seats: statistics.total,
    occupied_seats: statistics.occupied,
    available_seats: statistics.available,
    maintenance_seats: statistics.outOfOrder,
    disabled_seats: 0,
    occupancy_rate: statistics.occupancyRate,
  };

  /**
   * 좌석 상태 변경
   */
  const updateSeatStatus = useCallback(
    async (seatId: string, newStatus: SeatStatus, reason?: string) => {
      try {
        const existingSeat = seats.find((seat) => seat.id === seatId);
        if (!existingSeat) {
          throw new Error('좌석을 찾을 수 없습니다.');
        }

        const actionType = seatManagementService.determineActionType(
          existingSeat.status,
          newStatus
        );

        const success = await executeOwnerAction({
          action: actionType,
          seat_number: seatId,
          reason: reason,
        });

        if (!success) {
          throw new Error('좌석 상태 변경에 실패했습니다.');
        }

        // 성공 메시지
        if (actionType === 'force_checkout') {
          alert(`${seatId}번 좌석이 강제 체크아웃되었습니다.`);
        }

      } catch (error) {
        console.error('좌석 상태 변경 실패:', error);
        alert('좌석 상태를 변경하는데 실패했습니다.');
      }
    },
    [seats, executeOwnerAction]
  );

  /**
   * 모든 좌석을 사용 가능 상태로 초기화
   */
  const resetAllSeats = useCallback(async () => {
    try {
      // 모든 좌석에 대해 force_checkout 액션 실행
      const promises = seats.map((seat) =>
        executeOwnerAction({
          action: 'force_checkout',
          seat_number: seat.id,
          reason: '전체 초기화',
        })
      );

      await Promise.all(promises);
    } catch (error) {
      console.error('좌석 초기화 실패:', error);
      alert('좌석 초기화에 실패했습니다.');
    }
  }, [seats, executeOwnerAction]);

  /**
   * 좌석 클릭 핸들러
   */
  const handleSeatClick = useCallback(
    (seat: ManagedSeat) => {
      setSelectedSeat(realTimeSeats.find((s) => s.seat_number === seat.id) || null);
      setShowSeatModal(true);
    },
    [realTimeSeats]
  );

  /**
   * 좌석 상태 변경 핸들러
   */
  const handleStatusChange = useCallback(
    async (newStatus: SeatStatus) => {
      if (!selectedSeat) return;

      await updateSeatStatus(selectedSeat.seat_number, newStatus, newReason);
      setShowSeatModal(false);
      setNewReason('');
      setSelectedSeat(null);
    },
    [selectedSeat, newReason, updateSeatStatus]
  );

  /**
   * 모달 닫기
   */
  const closeSeatModal = useCallback(() => {
    setShowSeatModal(false);
    setNewReason('');
    setSelectedSeat(null);
  }, []);

  return {
    // 좌석 데이터
    seats,
    isLoading,
    error,
    lastRefreshTime,
    stats,

    // 좌석 액션
    updateSeatStatus,
    resetAllSeats,
    loadSeats,

    // 모달 관리
    selectedSeat,
    showSeatModal,
    newReason,
    setNewReason,
    handleSeatClick,
    handleStatusChange,
    closeSeatModal,
  };
};
